import { useState, useEffect, useMemo, useRef, useCallback, useId } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EllipsisVerticalIcon, XIcon } from 'lucide-react';
import type { ProjectWithPermissions, Endpoint, UpdateEndpointRequest } from '@mockd/shared';
import { TIER_LIMITS } from '@mockd/shared';
import { useProjects, useEndpoints, useRequestLogs, useAnalytics, useKeyboardShortcuts, useRules } from '../hooks';
import type { ShortcutDef } from '../hooks';
import { RequestList } from '../components/request';
import type { RequestListHandle } from '../components/request/RequestList';
import { RulesPanel } from '../components/rules';
import { QuickTestPanel, ExportEndpointModal, UsageSnippets } from '../components/endpoint';
import { Skeleton } from '../components/Skeleton';
import type { QuickTestHandle } from '../components/endpoint/QuickTestPanel';
import { AnalyticsDashboard } from '../components/analytics';
import { Alert, Breadcrumbs, CopyButton, ConfirmDialog, InlineEdit, HeadersEditor, ReadOnlyBadge, READ_ONLY_HINT, TabList, tabPanelProps } from '../components/common';
import { ShortcutHelpModal } from '../components/common/ShortcutHelpModal';
import { COMMON_CONTENT_TYPES } from '@mockd/shared/utils';
import { getMockApiSubdomainUrl, getProjectDoName } from '../config';

type EndpointTab = 'requests' | 'testing' | 'configuration' | 'analytics';

const TABS: { key: EndpointTab; label: string }[] = [
  { key: 'requests', label: 'Requests' },
  { key: 'testing', label: 'Testing' },
  { key: 'configuration', label: 'Configuration' },
  { key: 'analytics', label: 'Analytics' },
];

function AnalyticsTab({ projectId, endpointId }: { projectId: string; endpointId: string }) {
  const { analytics, loading: analyticsLoading, refetch: refetchAnalytics } = useAnalytics(projectId, endpointId);
  return (
    <AnalyticsDashboard
      analytics={analytics}
      loading={analyticsLoading}
      onRefresh={refetchAnalytics}
    />
  );
}

const DEMO_PAYLOAD = {
  event: 'payment.completed',
  data: {
    id: 'pay_8x4k2m',
    amount: 49.99,
    currency: 'USD',
    customer_email: 'jane@example.com',
  },
};

export function EndpointDetail() {
  const { projectId, endpointId } = useParams<{ projectId: string; endpointId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getProject } = useProjects();
  const { endpoints, fetchEndpoints, updateEndpoint, deleteEndpoint } = useEndpoints();

  const [project, setProject] = useState<ProjectWithPermissions | null>(null);
  const [endpoint, setEndpoint] = useState<Endpoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const requestListRef = useRef<RequestListHandle>(null);
  const quickTestRef = useRef<QuickTestHandle>(null);

  // Demo mode state
  const isDemo = searchParams.get('demo') === 'true';
  const [showDemoBanner, setShowDemoBanner] = useState(isDemo);
  const [demoStatus, setDemoStatus] = useState<'pending' | 'sent' | 'failed'>('pending');
  const demoFiredRef = useRef(false);

  // Get the DO name for WebSocket connection (subdomain for user-owned, id for anonymous)
  const doName = useMemo(() => project ? getProjectDoName(project) : undefined, [project]);

  // Stored history (paginated REST) merged with the live WebSocket stream
  const {
    status,
    requests,
    clearRequests,
    historyLoading,
    loadingMore,
    hasMore,
    historyError,
    loadMore,
    retryHistory,
  } = useRequestLogs({
    doName,
    endpointId: endpointId,
    projectId: projectId,
  });

  // From the project's effective tier — the team owner's on a team project — not
  // the viewer's own (#260). Unknown until the project loads, so no banner yet.
  const retentionDays = project ? TIER_LIMITS[project.effectiveTier].logRetentionDays : undefined;

  // The server's resolved permissions, as on ProjectDetail (#261): a viewer, or a
  // member who didn't create a team project, sees this endpoint read-only (#296).
  const canWrite = project?.permissions.canWrite ?? false;

  // Tab state from URL search params
  const activeTab = (searchParams.get('tab') as EndpointTab) || 'requests';
  const tabsId = useId();
  const setActiveTab = useCallback((tab: EndpointTab) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'requests') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
      setInspectorOpen(false);
    }
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const shortcuts: ShortcutDef[] = useMemo(() => [
    { key: '1', action: () => setActiveTab(TABS[0].key), label: 'Requests tab', group: 'Navigation' },
    { key: '2', action: () => setActiveTab(TABS[1].key), label: 'Testing tab', group: 'Navigation' },
    { key: '3', action: () => setActiveTab(TABS[2].key), label: 'Configuration tab', group: 'Navigation' },
    { key: '4', action: () => setActiveTab(TABS[3].key), label: 'Analytics tab', group: 'Navigation' },
    { key: 'j', action: () => requestListRef.current?.selectNext(), label: 'Next request', group: 'Navigation' },
    { key: 'k', action: () => requestListRef.current?.selectPrev(), label: 'Previous request', group: 'Navigation' },
    { key: '?', action: () => setShowShortcutHelp(prev => !prev), label: 'Show shortcuts', group: 'Actions' },
    { key: 'Mod+Enter', action: () => quickTestRef.current?.send(), label: 'Send test request', group: 'Actions' },
    ...(canWrite
      ? [{ key: 'Mod+Shift+K', action: () => requestListRef.current?.promptClear(), label: 'Clear request logs', group: 'Actions' } satisfies ShortcutDef]
      : []),
  ], [setActiveTab, canWrite]);

  useKeyboardShortcuts(shortcuts);

  // Rule names for the request inspector's matched-rules list. Refetched on returning
  // to the Requests tab, since RulesPanel edits rules through its own useRules.
  const { rules, fetchRules } = useRules();
  useEffect(() => {
    if (projectId && endpointId && activeTab === 'requests') fetchRules(projectId, endpointId);
  }, [projectId, endpointId, activeTab, fetchRules]);
  const ruleNames = useMemo(() => new Map(rules.map(r => [r.id, r.name])), [rules]);

  useEffect(() => {
    if (!projectId || !endpointId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [projectData] = await Promise.all([
          getProject(projectId),
          fetchEndpoints(projectId),
        ]);
        setProject(projectData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load endpoint');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, endpointId, getProject, fetchEndpoints]);

  // Find the endpoint from the fetched list
  useEffect(() => {
    if (endpointId && endpoints.length > 0) {
      const found = endpoints.find(e => e.id === endpointId);
      setEndpoint(found || null);
    }
  }, [endpointId, endpoints]);

  // Demo mode: auto-fire a demo request once WebSocket is connected
  useEffect(() => {
    if (!isDemo || demoFiredRef.current || status !== 'connected' || !endpoint || !project) {
      return;
    }

    demoFiredRef.current = true;
    const endpointDemoUrl = getMockApiSubdomainUrl(project.subdomain) + endpoint.path;

    // Small delay to ensure the WebSocket subscription is active
    const timer = setTimeout(() => {
      fetch(endpointDemoUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(DEMO_PAYLOAD),
      })
        .then((res) => setDemoStatus(res.ok ? 'sent' : 'failed'))
        // The demo is best-effort, but the banner mustn't claim it landed
        .catch(() => setDemoStatus('failed'));
    }, 300);

    return () => clearTimeout(timer);
  }, [isDemo, status, endpoint, project]);

  const dismissDemoBanner = () => {
    setShowDemoBanner(false);
    const params = new URLSearchParams(searchParams);
    params.delete('demo');
    setSearchParams(params);
  };

  if (loading) {
    return <Skeleton variant="endpoint-detail" />;
  }

  if (error || !project || !endpoint) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-error mb-4">{error || 'Endpoint not found'}</h1>
          <Link to={projectId ? `/projects/${projectId}` : '/'} className="link link-primary">
            Back to Project
          </Link>
        </div>
      </div>
    );
  }

  const mockBaseUrl = getMockApiSubdomainUrl(project.subdomain);
  const endpointUrl = mockBaseUrl + endpoint.path;

  const handleInlineSave = async (field: keyof UpdateEndpointRequest, value: string | number | boolean) => {
    if (!projectId || !endpointId) return;
    const updated = await updateEndpoint(projectId, endpointId, { [field]: value });
    setEndpoint(updated);
  };

  const handleHeadersSave = async (headers: Record<string, string> | null) => {
    if (!projectId || !endpointId) return;
    const updated = await updateEndpoint(projectId, endpointId, { responseHeaders: headers });
    setEndpoint(updated);
  };

  const handleDeleteEndpoint = async () => {
    if (!projectId || !endpointId) return;
    await deleteEndpoint(projectId, endpointId);
    navigate(`/projects/${projectId}`);
  };

  return (
    <div className="min-h-screen bg-base-200">
      <header className="bg-base-100 border-b border-base-300">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <Breadcrumbs
            items={[
              { label: 'Home', href: '/dashboard' },
              { label: project.name, href: `/projects/${projectId}` },
              { label: endpoint.path },
            ]}
          />
          <div className="flex items-center gap-4 mt-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base-content font-mono">{endpoint.path}</h1>
                {!canWrite && <ReadOnlyBadge />}
              </div>
              <span className="inline-flex items-center gap-1 mt-1">
                <code className="text-sm font-mono text-base-content/80 break-all">{endpointUrl}</code>
                <CopyButton text={endpointUrl} iconOnly />
              </span>
            </div>
            <div className="ml-auto">
              <div className="dropdown dropdown-end">
                <button tabIndex={0} className="btn btn-sm btn-ghost btn-square" aria-label="Endpoint settings">
                  <EllipsisVerticalIcon className="size-5" />
                </button>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 bg-base-100 border border-base-300 rounded-box w-40">
                  <li>
                    <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowExport(true); }}>
                      Export
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowDeleteConfirm(true); }}
                      disabled={!canWrite}
                      title={canWrite ? undefined : READ_ONLY_HINT}
                      className="text-error"
                    >
                      Delete
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className={`${inspectorOpen ? 'max-w-[1600px]' : 'max-w-7xl'} mx-auto px-4 py-8 sm:px-6 lg:px-8 transition-all duration-200`}>
        {showDemoBanner && (
          <Alert
            variant={demoStatus === 'failed' ? 'warning' : 'success'}
            className="mb-6"
            title="Your mock endpoint is live"
            action={
              <>
                <CopyButton
                  text={`curl -X POST ${endpointUrl} -H "Content-Type: application/json" -d '${JSON.stringify(DEMO_PAYLOAD)}'`}
                  label="Copy"
                  className="btn-sm"
                />
                <button
                  onClick={dismissDemoBanner}
                  className="btn btn-ghost btn-sm btn-square"
                  aria-label="Dismiss demo banner"
                >
                  <XIcon className="size-4" />
                </button>
              </>
            }
          >
            <p>
              {demoStatus === 'sent'
                ? 'We sent a demo webhook request — you can see it in the Request Stream below. Try sending your own:'
                : demoStatus === 'failed'
                  ? "We couldn't send the demo request. Try sending your own:"
                  : 'Sending a demo webhook request… You can also send your own:'}
            </p>
            <code className={`font-mono block mt-1.5 ${demoStatus === 'failed' ? 'bg-warning/20' : 'bg-success/20'} px-2 py-1 rounded-field break-all`}>
              curl -X POST {endpointUrl} -H "Content-Type: application/json" -d '{JSON.stringify({ event: "test" })}'
            </code>
          </Alert>
        )}

        {/* Tab Navigation */}
        <TabList
          tabs={TABS}
          active={activeTab}
          onChange={setActiveTab}
          idPrefix={tabsId}
          className="tabs tabs-border mb-6 flex-nowrap overflow-x-auto"
          tabClassName="tab-lg"
        />

        {/* Requests Tab — always mounted, hidden when inactive (preserves WebSocket) */}
        <div {...tabPanelProps(tabsId, 'requests')} className={activeTab === 'requests' ? '' : 'hidden'}>
          <RequestList
            ref={requestListRef}
            requests={requests}
            status={status}
            onClear={clearRequests}
            endpointUrl={endpointUrl}
            mockBaseUrl={mockBaseUrl}
            onInspectorChange={setInspectorOpen}
            historyLoading={historyLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            historyError={historyError}
            onLoadMore={loadMore}
            onRetryHistory={retryHistory}
            retentionDays={retentionDays}
            canClear={canWrite}
            ruleNames={ruleNames}
          />
        </div>

        {/* Testing Tab */}
        {activeTab === 'testing' && (
          <div {...tabPanelProps(tabsId, 'testing')} className="space-y-6">
            <QuickTestPanel ref={quickTestRef} endpointUrl={endpointUrl} />
            <UsageSnippets endpointUrl={endpointUrl} endpoint={endpoint} />
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div {...tabPanelProps(tabsId, 'configuration')} className="space-y-6">
            {/* Response Card */}
            <div className="card bg-base-100 p-4">
              <h2 className="text-base-content mb-3">Response</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-base-content/80 block mb-0.5">Status Code</span>
                  <InlineEdit
                    readOnly={!canWrite}
                    type="number"
                    value={endpoint.statusCode}
                    label="Status Code"
                    min={100}
                    max={599}
                    onSave={(v) => handleInlineSave('statusCode', v)}
                  />
                </div>
                <div>
                  <span className="text-xs text-base-content/80 block mb-0.5">Content Type</span>
                  <InlineEdit
                    readOnly={!canWrite}
                    type="select"
                    value={endpoint.contentType}
                    label="Content Type"
                    options={COMMON_CONTENT_TYPES.map(ct => ({ value: ct, label: ct }))}
                    onSave={(v) => handleInlineSave('contentType', v)}
                  />
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-base-200">
                <HeadersEditor
                  value={endpoint.responseHeaders}
                  onChange={handleHeadersSave}
                  label="Response Headers"
                  placeholder={{ key: 'X-Custom-Header', value: 'value' }}
                  readOnly={!canWrite}
                />
              </div>

              <div className="mt-4 pt-4 border-t border-base-200">
                <span className="text-xs text-base-content/80 block mb-1">Response Body</span>
                <InlineEdit
                  readOnly={!canWrite}
                  type="text"
                  value={endpoint.responseBody}
                  label="Response Body"
                  multiline
                  rows={6}
                  mono
                  onSave={(v) => handleInlineSave('responseBody', v)}
                />
              </div>
            </div>

            {/* Network Bar */}
            <div className="card bg-base-100 px-4 py-2.5">
              <div className="flex items-center gap-6">
                <span className="text-xs font-semibold text-base-content/60 uppercase tracking-wider shrink-0">Network</span>
                <div className="flex items-center gap-6 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-base-content/60">
                      {endpoint.delay > 0 && endpoint.delayMax > 0 ? 'Delay Range' : 'Delay'}
                    </span>
                    <InlineEdit
                      readOnly={!canWrite}
                      type="number"
                      value={endpoint.delay}
                      label="Delay"
                      min={0}
                      displayValue={endpoint.delay > 0 ? `${endpoint.delay}ms` : 'None'}
                      onSave={(v) => handleInlineSave('delay', v)}
                    />
                    {(endpoint.delay > 0 || endpoint.delayMax > 0) && (
                      <>
                        <span className="text-xs text-base-content/60">–</span>
                        <InlineEdit
                          readOnly={!canWrite}
                          type="number"
                          value={endpoint.delayMax}
                          label="Max Delay"
                          min={0}
                          displayValue={endpoint.delayMax > 0 ? `${endpoint.delayMax}ms` : 'None'}
                          onSave={(v) => handleInlineSave('delayMax', v)}
                        />
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-base-content/60">Rate Limit</span>
                    <InlineEdit
                      readOnly={!canWrite}
                      type="number"
                      value={endpoint.rateLimit}
                      label="Rate Limit"
                      min={1}
                      suffix=" req/min"
                      onSave={(v) => handleInlineSave('rateLimit', v)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <RulesPanel
              projectId={projectId!}
              endpointId={endpointId!}
              canWrite={canWrite}
            />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div {...tabPanelProps(tabsId, 'analytics')}>
            <AnalyticsTab
              projectId={projectId!}
              endpointId={endpointId!}
            />
          </div>
        )}
      </main>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Endpoint"
        message="Are you sure you want to delete this endpoint? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteEndpoint}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ShortcutHelpModal
        isOpen={showShortcutHelp}
        onClose={() => setShowShortcutHelp(false)}
        shortcuts={shortcuts}
      />

      <ExportEndpointModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        projectId={projectId!}
        projectName={project.name}
        baseUrl={getMockApiSubdomainUrl(project.subdomain)}
        endpoints={endpoints}
        singleEndpointId={endpointId}
      />
    </div>
  );
}
